import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Service for managing access requests (family join requests)
 */
class AccessRequestService {
  /**
   * Create a new access request to join a family
   * @param {string} userId - User ID requesting access
   * @param {string} familyCode - Family code to join
   * @param {string} userEmail - User's email
   * @param {string} userName - User's display name
   * @returns {Promise<string>} Request ID
   */
  async createFamilyJoinRequest(userId, familyCode, userEmail, userName) {
    if (!userId || !familyCode) {
      throw new Error('User ID and family code are required');
    }

    // Check if family exists
    const familyRef = doc(db, 'families', familyCode);
    const familyDoc = await getDoc(familyRef);

    if (!familyDoc.exists()) {
      throw new Error('Family code not found. Please check and try again.');
    }

    const familyData = familyDoc.data();

    // Check if user already has a pending request for this family
    // Use NESTED structure: /families/{familyCode}/accessRequests/
    const existingRequestQuery = query(
      collection(db, 'families', familyCode, 'accessRequests'),
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    const existingRequests = await getDocs(existingRequestQuery);

    if (!existingRequests.empty) {
      throw new Error('You already have a pending request for this family.');
    }

    // Create the access request in NESTED structure
    const requestData = {
      userId,
      familyCode,
      familyName: familyData.name || 'Unknown Family',
      userEmail,
      userName,
      type: 'family_join', // Type of request
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const requestRef = await addDoc(collection(db, 'families', familyCode, 'accessRequests'), requestData);
    return requestRef.id;
  }

  /**
   * Get all pending access requests for a family
   * @param {string} familyCode - Family code
   * @returns {Promise<Array>} Array of pending requests
   */
  async getFamilyAccessRequests(familyCode) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    // Use NESTED structure: /families/{familyCode}/accessRequests/
    const q = query(
      collection(db, 'families', familyCode, 'accessRequests'),
      where('status', '==', 'pending')
    );

    const querySnapshot = await getDocs(q);
    const requests = [];

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return requests;
  }

  /**
   * Get all pending requests for a user
   * NOTE: This requires querying across all families since requests are now nested
   * For better performance, pass familyCode if known
   * @param {string} userId - User ID
   * @param {string} familyCode - Optional family code to narrow search
   * @returns {Promise<Array>} Array of user's pending requests
   */
  async getUserAccessRequests(userId, familyCode = null) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // If familyCode provided, use NESTED structure for efficient query
    if (familyCode) {
      const q = query(
        collection(db, 'families', familyCode, 'accessRequests'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);
      const requests = [];

      querySnapshot.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return requests;
    }

    // If no familyCode, need to query all families
    // This is less efficient but necessary for cross-family queries
    // Get all families first
    const familiesSnapshot = await getDocs(collection(db, 'families'));
    const allRequests = [];

    // Query each family's accessRequests subcollection
    for (const familyDoc of familiesSnapshot.docs) {
      const q = query(
        collection(db, 'families', familyDoc.id, 'accessRequests'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        allRequests.push({
          id: doc.id,
          ...doc.data()
        });
      });
    }

    return allRequests;
  }

  /**
   * Approve an access request
   * @param {string} requestId - Request ID
   * @param {string} familyCode - Family code
   * @param {string} approvedBy - User ID of approver
   * @returns {Promise<void>}
   */
  async approveRequest(requestId, familyCode, approvedBy) {
    if (!requestId || !familyCode || !approvedBy) {
      throw new Error('Request ID, family code, and approver ID are required');
    }

    // Use NESTED structure: /families/{familyCode}/accessRequests/{requestId}
    const requestRef = doc(db, 'families', familyCode, 'accessRequests', requestId);
    const requestDoc = await getDoc(requestRef);

    if (!requestDoc.exists()) {
      throw new Error('Request not found');
    }

    const requestData = requestDoc.data();

    // Update request status
    await updateDoc(requestRef, {
      status: 'approved',
      approvedBy,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return requestData;
  }

  /**
   * Deny an access request
   * @param {string} requestId - Request ID
   * @param {string} familyCode - Family code
   * @param {string} deniedBy - User ID of denier
   * @param {string} reason - Optional reason for denial
   * @returns {Promise<void>}
   */
  async denyRequest(requestId, familyCode, deniedBy, reason = '') {
    if (!requestId || !familyCode || !deniedBy) {
      throw new Error('Request ID, family code, and denier ID are required');
    }

    // Use NESTED structure: /families/{familyCode}/accessRequests/{requestId}
    const requestRef = doc(db, 'families', familyCode, 'accessRequests', requestId);
    const requestDoc = await getDoc(requestRef);

    if (!requestDoc.exists()) {
      throw new Error('Request not found');
    }

    // Update request status
    await updateDoc(requestRef, {
      status: 'denied',
      deniedBy,
      deniedAt: serverTimestamp(),
      denialReason: reason,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Cancel an access request (by the requester)
   * @param {string} requestId - Request ID
   * @param {string} familyCode - Family code
   * @returns {Promise<void>}
   */
  async cancelRequest(requestId, familyCode) {
    if (!requestId || !familyCode) {
      throw new Error('Request ID and family code are required');
    }

    // Use NESTED structure: /families/{familyCode}/accessRequests/{requestId}
    const requestRef = doc(db, 'families', familyCode, 'accessRequests', requestId);
    await deleteDoc(requestRef);
  }
}

export const accessRequestService = new AccessRequestService();
export default accessRequestService;
